import React from "react";
import { Route, Switch } from "react-router-dom";
import { useSelector } from "react-redux";
import { ClientsPage } from "./pages/ClientsPage/ClientsPage";
import { SystemSettingsPage } from "./pages/SystemSettingsPage/SystemSettingsPage";
import { ClientDetailsPage } from "./pages/ClientsPage/ClientDetailsPage/ClientDetailsPage";
import { ApplicationState } from "./store/store";
import { RestrictedPage } from "./components/RestrictedPage/RestrictedPage";
import { NavMenuOption } from "./components/PageLayout/PageLayout";
import Button from "@mui/material/Button";
import { signOut } from "aws-amplify/auth";
import { EmailJobRequestsPage } from "./pages/JobRequests/EmailJobRequestsPage/EmailJobRequestsPage";
import { EmailJobRequestDetailsPage } from "./pages/JobRequests/EmailJobRequestsPage/EmailJobRequestDetailsPage/EmailJobRequestDetailsPage";
import { QuoteJobRequestsPage } from "./pages/JobRequests/QuoteJobRequestsPage/QuoteJobRequestsPage";
import { PagePath } from "./types/PagePath";
import { JobRequestOrigination, UserGroup } from "./gen/clients";
import { AnalyticsPage } from "./pages/AnalyticsPage/AnalyticsPage";
import { MachineTranslationAnalyticsPage } from "./pages/AnalyticsPage/MachineTranslationAnalyticsPage/MachineTranslationAnalyticsPage";
import { MachineTranslationClientUsageAnalyticsPage } from "./pages/AnalyticsPage/MachineTranslationClientUsageAnalyticsPage/MachineTranslationClientUsageAnalyticsPage";
import { MTJobRequestDetailsPage } from "./pages/JobRequests/MTJobRequestsPage/MTJobRequestDetailsPage/MTJobRequestDetailsPage";
import { EnabledApplicationAnalyticsPage } from "pages/AnalyticsPage/EnabledApplicationsAnalyticsPage/EnabledApplicationsAnalyticsPage";
import { QuoteJobRequestDetailsPage } from "./pages/JobRequests/QuoteJobRequestsPage/QuoteJobRequestDetailsPage/QuoteJobRequestDetailsPage";
import { AutoQuoteRejectionReasonsAnalyticsPage } from "pages/AnalyticsPage/AutoQuoteRejectionReasonsAnalyticsPage/AutoQuoteRejectionReasonsAnalyticsPage";
import { MTJobRequestsPage } from "./pages/JobRequests/MTJobRequestsPage/MTJobRequestsPage";
import { StaffAccountsPage } from "./pages/StaffAccountsPage/StaffAccountsPage";
import { ClientContactsReportsPage } from "./pages/AnalyticsPage/ClientContactsReportsPage/ClientContactsReportsPage";
import { ClientContactsReportDetailsPage } from "./pages/AnalyticsPage/ClientContactsReportsPage/ClientContactsReportDetailsPage/ClientContactsReportDetailsPage";
import { FileTypesByClientReportsPage } from "./pages/AnalyticsPage/FileTypesByClientReportsPage/FileTypesByClientReportsPage";
import { FileTypesByClientReportDetailsPage } from "./pages/AnalyticsPage/FileTypesByClientReportsPage/FileTypesByClientReportDetailsPage/FileTypesByClientReportDetailsPage";
import { ExternalApiJobRequestsPage } from "pages/JobRequests/ExternalApiJobRequestsPage/ExternalApiJobRequestsPage";
import { ExternalApiJobRequestDetailsPage } from "pages/JobRequests/ExternalApiJobRequestsPage/ExternalApiJobRequestDetailsPage/ExternalApiJobRequestDetailsPage";
import { MtEscalationAnalyticsPage } from "./pages/AnalyticsPage/MtEscalationAnalyticsPage/MtEscalationAnalyticsPage";
import { MtEscalationByClientContactAnalyticsPage } from "./pages/AnalyticsPage/MtEscalationByClientContactAnalyticsPage/MtEscalationByClientContactAnalyticsPage";
import { ApplicationEventAnalyticsPage } from "./pages/AnalyticsPage/ApplicationEventAnalyticsPage/ApplicationEventAnalyticsPage";
import { AiServiceUsageAnalyticsPage } from "./pages/AnalyticsPage/AiServiceUsageAnalyticsPage/AiServiceUsageAnalyticsPage";
import { LqaRatingAnalyticsPage } from "./pages/AnalyticsPage/LqaRatingAnalyticsPage/LqaRatingAnalyticsPage";
import { LqaRatingReportDetailsPage } from "./pages/AnalyticsPage/LqaRatingAnalyticsPage/LqaRatingReportDetailsPage/LqaRatingReportDetailsPage";
import { MtUsageForClientBillingAnalyticsPage } from "./pages/AnalyticsPage/MtUsageForClientBillingAnalyticsPage/MtUsageForClientBillingAnalyticsPage";

const Routes: React.FC = () => {
  const { username, roles } = useSelector((state: ApplicationState) => ({
    roles: state.userSession.roles || [],
    username: state.userSession.username
  }));
  const isAdminUser = roles.includes(UserGroup.ADMIN) || roles.includes(UserGroup.SUPER_USER);
  const isOperatorUser = roles.includes(UserGroup.OPERATOR);
  const isExecutiveUser = roles.includes(UserGroup.EXECUTIVE);
  const adminGroups = [UserGroup.ADMIN, UserGroup.SUPER_USER];
  const operatorGroups = [UserGroup.OPERATOR, UserGroup.ADMIN, UserGroup.SUPER_USER];

  const onSignOutClick = React.useCallback(() => signOut(), []);

  if (isAdminUser || isOperatorUser || isExecutiveUser) {
    return (
      <Switch>
        <Route path={`${PagePath.clients}`} exact={true}>
          <RestrictedPage allowedRoles={adminGroups} navMenuOption={NavMenuOption.CLIENTS}>
            <ClientsPage />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.clients}/:clientId/:tabId?`}>
          <RestrictedPage allowedRoles={adminGroups} navMenuOption={NavMenuOption.CLIENTS}>
            <ClientDetailsPage />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.emailJobRequests}`} exact={true}>
          <RestrictedPage allowedRoles={operatorGroups} navMenuOption={NavMenuOption.EMAIL_JOB_REQUESTS}>
            <EmailJobRequestsPage />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.emailJobRequests}/:jobRequestId`}>
          <RestrictedPage allowedRoles={operatorGroups} navMenuOption={NavMenuOption.EMAIL_JOB_REQUESTS}>
            <EmailJobRequestDetailsPage origination={JobRequestOrigination.EMAIL} />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.mtJobRequests}/:tabId?`} exact={true}>
          <RestrictedPage allowedRoles={operatorGroups} navMenuOption={NavMenuOption.MT_JOB_REQUESTS}>
            <MTJobRequestsPage />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.mtJobRequests}/:tabId/:jobRequestId`} exact={true}>
          <RestrictedPage allowedRoles={operatorGroups} navMenuOption={NavMenuOption.MT_JOB_REQUESTS}>
            <MTJobRequestDetailsPage />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.externalApiJobRequests}`} exact={true}>
          <RestrictedPage allowedRoles={operatorGroups} navMenuOption={NavMenuOption.EXTERNAL_API_JOB_REQUESTS}>
            <ExternalApiJobRequestsPage />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.externalApiJobRequests}/:jobRequestId`} exact={true}>
          <RestrictedPage allowedRoles={operatorGroups} navMenuOption={NavMenuOption.EXTERNAL_API_JOB_REQUESTS}>
            <ExternalApiJobRequestDetailsPage />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.quoteJobRequests}`} exact={true}>
          <RestrictedPage allowedRoles={operatorGroups} navMenuOption={NavMenuOption.QUOTE_JOB_REQUESTS}>
            <QuoteJobRequestsPage />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.quoteJobRequests}/:jobRequestId`}>
          <RestrictedPage allowedRoles={operatorGroups} navMenuOption={NavMenuOption.QUOTE_JOB_REQUESTS}>
            <QuoteJobRequestDetailsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/machineTranslationRequestCount" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <MachineTranslationAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/machineTranslationClientUsageAnalytics" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <MachineTranslationClientUsageAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/enabledApplicationsAnalytics" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <EnabledApplicationAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/quoteRejectionReasonsAnalytics" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <AutoQuoteRejectionReasonsAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/mtEscalationAnalytics" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <MtEscalationAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/mtEscalationByClientContact" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <MtEscalationByClientContactAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/aiServiceUsageAnalytics" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <AiServiceUsageAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/lqaRatingAnalytics/:reportId" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <LqaRatingReportDetailsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/lqaRatingAnalytics" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <LqaRatingAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/applicationEventAnalytics" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <ApplicationEventAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/clientContactsReports/:reportId" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <ClientContactsReportDetailsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/clientContactsReports" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <ClientContactsReportsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/fileTypesByClientReports/:reportId" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <FileTypesByClientReportDetailsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/fileTypesByClientReports" exact={true}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <FileTypesByClientReportsPage />
          </RestrictedPage>
        </Route>
        <Route path="/analytics/mtUsageForClientBilling" exact={false}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <MtUsageForClientBillingAnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path={PagePath.analytics} exact={false}>
          <RestrictedPage
            allowedRoles={[...operatorGroups, UserGroup.EXECUTIVE]}
            navMenuOption={NavMenuOption.ANALYTICS}
          >
            <AnalyticsPage />
          </RestrictedPage>
        </Route>
        <Route path={PagePath.staffAccounts}>
          <RestrictedPage
            allowedRoles={[UserGroup.SUPER_USER, UserGroup.ADMIN]}
            navMenuOption={NavMenuOption.STAFF_ACCOUNTS}
          >
            <StaffAccountsPage />
          </RestrictedPage>
        </Route>
        <Route path={`${PagePath.systemSettings}/:tabId`}>
          <RestrictedPage allowedRoles={[UserGroup.SUPER_USER]} navMenuOption={NavMenuOption.SYSTEM_SETTINGS}>
            <SystemSettingsPage />
          </RestrictedPage>
        </Route>
        <Route path={PagePath.systemSettings}>
          <RestrictedPage allowedRoles={[UserGroup.SUPER_USER]} navMenuOption={NavMenuOption.SYSTEM_SETTINGS}>
            <SystemSettingsPage />
          </RestrictedPage>
        </Route>
        {isAdminUser && <Route path="/" component={ClientsPage} />}
        {isOperatorUser && <Route path="/" component={EmailJobRequestsPage} />}
        {isExecutiveUser && <Route path="/" component={AnalyticsPage} />}
      </Switch>
    );
  }
  return (
    <div style={{ marginLeft: 20 }}>
      {`You signed in as "${username}". Your account does not have sufficient permissions to access this application.`}
      <Button variant="text" onClick={onSignOutClick}>
        Sign out
      </Button>
    </div>
  );
};

export default Routes;
